import axios from 'axios';
import { getContextHeaders } from './LinkPagamentoService';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const apiKey = process.env.REACT_APP_OPEN_API_KEY;
/**
 * URL do bundle do SDK C6PaySDK. A doc oficial (`GET /sdk-doc`) ainda não publica
 * uma URL absoluta do script ("instalação indisponível no momento"), então a URL
 * é configurável via .env — ver README.
 */
const c6SdkUrl = process.env.REACT_APP_C6_SDK_URL;
/** Ambiente do SDK/antifraude do C6: 'homolog' (default) ou 'prod'. */
const c6SdkEnv: 'homolog' | 'prod' =
  process.env.REACT_APP_C6_SDK_ENV === 'prod' ? 'prod' : 'homolog';

const CreditCardApi = axios.create({
  baseURL: `${backendUrl}/open-api/v1/payment-links`,
  headers: {
    'Content-Type': 'application/json',
    ...(apiKey && { 'x-api-key': apiKey }),
  },
});

declare global {
  interface Window {
    C6PaySDK?: {
      init?: (opts: { env: 'homolog' | 'prod'; transactionId: string }) => Promise<void>;
      createClient: (session: { publicKey: string; sessionKey: string }) => {
        generateCardHash: (card: C6CardInput) => Promise<string>;
      };
      setDebugMode?: (flag: boolean) => void;
      setLogger?: (fn: (level: string, message: string, context?: unknown) => void) => void;
    };
  }
}

/**
 * Shape do objeto `card` esperado pelo `C6PaySDK.createClient(...).generateCardHash(card)`
 * (conforme `GET /sdk-doc` do C6). Estes dados NUNCA saem do browser — servem apenas
 * para gerar o card hash localmente via Web Crypto API.
 */
export interface C6CardInput {
  number: string;
  holderName: string;
  /** MM */
  expirationMonth: string;
  /** AAAA */
  expirationYear: string;
  cvv: string;
  /** Ex.: VISA, MASTERCARD, ELO */
  brand: string;
}

export interface EnderecoPagadorCartao {
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
}

export interface PagadorCartao {
  nome: string;
  cpfCnpj: string;
  email: string;
  telefone: string;
  endereco: EnderecoPagadorCartao;
}

/** Entrada do fluxo de pagamento com cartão montada pelo checkout. */
export interface CreditCardSubmission {
  linkPagamentoId: string;
  qtdParcelas: number;
  /** Dados abertos do cartão — usados SOMENTE no browser para gerar o card hash. */
  card: C6CardInput;
  pagador: PagadorCartao;
}

export type CreditCardAuthorizeStatus = 'pago' | 'autorizado' | 'pendente' | 'recusado' | 'erro';

export interface CreditCardAuthorizeResult {
  status: CreditCardAuthorizeStatus;
  checkoutId: string;
  codigoAutorizacao?: string;
  mensagem?: string;
  cartao?: {
    numeroMascarado: string;
    bandeira: string;
  };
}

/** Entrada do fluxo de fallback hosted (o pagador digita o cartão na página do C6). */
export interface HostedCheckoutInput {
  linkPagamentoId: string;
  qtdParcelas: number;
  pagador: PagadorCartao;
  /** URL desta página — o C6 traz o pagador de volta para cá após o pagamento. */
  redirectUrl: string;
}

export interface HostedCheckoutResult {
  checkoutId: string;
  url: string;
}

/**
 * Erro que indica que o SDK transparente do C6 NÃO está disponível
 * (URL não configurada, browser sem Web Crypto API ou falha ao carregar o script).
 * É o gatilho do fallback para o checkout hosted.
 */
export class C6SdkUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'C6SdkUnavailableError';
  }
}

/**
 * Decisão hosted × transparente: o checkout transparente só é usado quando a URL
 * do bundle do SDK C6 está configurada. Sem ela, o fluxo cai no checkout hosted.
 */
export function isTransparentCheckoutAvailable(): boolean {
  return Boolean(c6SdkUrl);
}

interface PublicKeyResponse {
  publicKey: string;
  sessionKey: string;
  expiresIn: number;
}

function getErrorMessage(error: any): string {
  const erro = error.response?.data?.erro;
  if (erro?.mensagem) return erro.mensagem;
  if (error.response?.data?.message) return error.response.data.message;
  return 'Erro na requisição';
}

let sdkLoadPromise: Promise<NonNullable<Window['C6PaySDK']>> | null = null;

/**
 * Carrega o SDK do C6 dinamicamente (só quando o método cartão é usado),
 * com a checagem de suporte à Web Crypto API recomendada pela doc do SDK.
 */
function loadC6Sdk(): Promise<NonNullable<Window['C6PaySDK']>> {
  if (window.C6PaySDK) return Promise.resolve(window.C6PaySDK);
  if (!window.crypto?.subtle) {
    return Promise.reject(
      new C6SdkUnavailableError(
        'Seu navegador não suporta criptografia segura (Web Crypto API). Atualize o navegador ou use outro dispositivo.'
      )
    );
  }
  if (!c6SdkUrl) {
    return Promise.reject(new C6SdkUnavailableError('REACT_APP_C6_SDK_URL não configurada no .env'));
  }
  if (!sdkLoadPromise) {
    sdkLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = c6SdkUrl;
      script.async = true;
      script.onload = () => {
        if (window.C6PaySDK) {
          resolve(window.C6PaySDK);
        } else {
          sdkLoadPromise = null;
          reject(new C6SdkUnavailableError('SDK de pagamento carregado, mas indisponível. Tente novamente.'));
        }
      };
      script.onerror = () => {
        sdkLoadPromise = null;
        reject(
          new C6SdkUnavailableError(
            'Não foi possível carregar o SDK de pagamento. Verifique sua conexão e tente novamente.'
          )
        );
      };
      document.head.appendChild(script);
    });
  }
  return sdkLoadPromise;
}

let sdkInitializedFor: string | null = null;

/**
 * `init({ env, transactionId })` é obrigatório antes de gerar o hash (antifraude do C6).
 * Inicializa uma vez por transação (linkPagamentoId).
 */
async function ensureSdkInitialized(
  sdk: NonNullable<Window['C6PaySDK']>,
  transactionId: string
): Promise<void> {
  if (typeof sdk.init !== 'function') return;
  if (sdkInitializedFor === transactionId) return;
  await sdk.init({ env: c6SdkEnv, transactionId });
  sdkInitializedFor = transactionId;
}

/** Mensagens amigáveis para os erros documentados do SDK (sem expor dados sensíveis). */
function mapSdkErrorMessage(error: any): string {
  const message: string = error?.message || '';
  if (message.includes('publicKey ausente')) return 'Chave pública não informada ou inválida.';
  if (message.includes('sessionKey ausente')) return 'Sessão de pagamento inválida. Tente novamente.';
  if (message.includes('Campo obrigatório ausente')) {
    return 'Preencha todos os campos do cartão corretamente.';
  }
  if (message.includes('Web Crypto API não suportada')) {
    return 'Seu navegador não suporta criptografia segura (Web Crypto API).';
  }
  return message || 'Erro ao processar os dados do cartão.';
}

/**
 * Busca a chave pública + sessionKey no backend NexxusOne.
 * A chave PEM expira em ~600s — buscar na hora do submit, nunca no load da página.
 */
async function obterChavePublica(
  linkPagamentoId: string,
  empresa?: string | null,
  filial?: string | null
): Promise<PublicKeyResponse> {
  try {
    const response = await CreditCardApi.get(`/credit-card/public-key/${linkPagamentoId}`, {
      headers: getContextHeaders(empresa, filial),
    });
    const dados = response.data?.dados;
    if (!dados?.publicKey || !dados?.sessionKey) {
      throw new Error('Resposta inválida ao obter a chave de criptografia do pagamento.');
    }
    return dados as PublicKeyResponse;
  } catch (error: any) {
    if (error.response) throw new Error(getErrorMessage(error));
    throw error;
  }
}

/**
 * Processa o pagamento com cartão de crédito (checkout transparente C6):
 * 1. Busca publicKey/sessionKey no backend (validade ~600s);
 * 2. Gera o card hash NO BROWSER com o C6PaySDK (dados abertos nunca saem daqui);
 * 3. Envia SOMENTE o cardHash + sessionKey + pagador para o authorize do backend.
 */
export async function processarCartao(
  submission: CreditCardSubmission,
  empresa?: string | null,
  filial?: string | null
): Promise<CreditCardAuthorizeResult> {
  if (!apiKey) throw new Error('REACT_APP_OPEN_API_KEY não configurada no .env');

  const sdk = await loadC6Sdk();
  const { publicKey, sessionKey } = await obterChavePublica(submission.linkPagamentoId, empresa, filial);
  await ensureSdkInitialized(sdk, submission.linkPagamentoId);

  let cardHash: string;
  try {
    const client = sdk.createClient({ publicKey, sessionKey });
    cardHash = await client.generateCardHash(submission.card);
  } catch (sdkError: any) {
    throw new Error(mapSdkErrorMessage(sdkError));
  }

  try {
    const response = await CreditCardApi.post(
      '/credit-card/authorize',
      {
        linkPagamentoId: submission.linkPagamentoId,
        qtdParcelas: submission.qtdParcelas,
        cardHash,
        sessionKey,
        pagador: submission.pagador,
      },
      { headers: getContextHeaders(empresa, filial) }
    );
    const dados = response.data?.dados;
    if (!dados?.status) {
      throw new Error('Resposta inválida do processamento do cartão.');
    }
    return dados as CreditCardAuthorizeResult;
  } catch (error: any) {
    if (error.response) throw new Error(getErrorMessage(error));
    throw error;
  }
}

/**
 * Fallback de checkout HOSTED do C6 — usado quando o SDK transparente não está
 * disponível (`isTransparentCheckoutAvailable() === false` ou falha ao carregar
 * o script). O backend cria o checkout no C6 e devolve a URL da página de
 * pagamento do banco; o front redireciona a MESMA janela para lá. Nenhum dado
 * de cartão é coletado no nosso checkout neste fluxo, e o histórico é
 * registrado pelo backend.
 */
export async function criarCheckoutHosted(
  input: HostedCheckoutInput,
  empresa?: string | null,
  filial?: string | null
): Promise<HostedCheckoutResult> {
  if (!apiKey) throw new Error('REACT_APP_OPEN_API_KEY não configurada no .env');
  try {
    const response = await CreditCardApi.post('/credit-card/checkout-hosted', input, {
      headers: getContextHeaders(empresa, filial),
    });
    const dados = response.data?.dados;
    if (!dados?.url) {
      throw new Error('Resposta inválida ao criar o checkout de pagamento.');
    }
    return dados as HostedCheckoutResult;
  } catch (error: any) {
    if (error.response) throw new Error(getErrorMessage(error));
    throw error;
  }
}
